import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Country, State, City } from 'country-state-city';
import { Check, ChevronsUpDown, MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Country-aware postal/ZIP validation ──────────────────────────
 * Map ISO country code → { regex, example, label }
 * Covers ~30 most-common countries. Default: 3-12 alphanumeric chars.
 * Empty ZIP is always allowed (the field is optional).
 */
const ZIP_RULES = {
  US: { re: /^\d{5}(-\d{4})?$/i,         example: '12345 or 12345-6789' },
  CA: { re: /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/i, example: 'A1A 1A1' },
  GB: { re: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, example: 'SW1A 1AA' },
  IN: { re: /^\d{6}$/,                   example: '110001' },
  AU: { re: /^\d{4}$/,                   example: '3810' },
  NZ: { re: /^\d{4}$/,                   example: '6011' },
  DE: { re: /^\d{5}$/,                   example: '10115' },
  FR: { re: /^\d{5}$/,                   example: '75001' },
  IT: { re: /^\d{5}$/,                   example: '00100' },
  ES: { re: /^\d{5}$/,                   example: '28001' },
  PT: { re: /^\d{4}-\d{3}$/,             example: '1000-001' },
  NL: { re: /^\d{4}\s?[A-Z]{2}$/i,       example: '1011 AB' },
  BE: { re: /^\d{4}$/,                   example: '1000' },
  CH: { re: /^\d{4}$/,                   example: '8001' },
  AT: { re: /^\d{4}$/,                   example: '1010' },
  SE: { re: /^\d{3}\s?\d{2}$/,           example: '114 51' },
  NO: { re: /^\d{4}$/,                   example: '0150' },
  DK: { re: /^\d{4}$/,                   example: '1050' },
  FI: { re: /^\d{5}$/,                   example: '00100' },
  IE: { re: /^[A-Z]\d{2}\s?[A-Z\d]{4}$/i, example: 'D02 X285' },
  PL: { re: /^\d{2}-\d{3}$/,             example: '00-001' },
  JP: { re: /^\d{3}-\d{4}$/,             example: '100-0001' },
  KR: { re: /^\d{5}$/,                   example: '06236' },
  CN: { re: /^\d{6}$/,                   example: '100000' },
  TW: { re: /^\d{3}(\d{2})?$/,           example: '100 or 10010' },
  HK: { re: /^$/,                        example: '(none)' },
  SG: { re: /^\d{6}$/,                   example: '238859' },
  MY: { re: /^\d{5}$/,                   example: '50450' },
  TH: { re: /^\d{5}$/,                   example: '10100' },
  ID: { re: /^\d{5}$/,                   example: '10110' },
  PH: { re: /^\d{4}$/,                   example: '1226' },
  VN: { re: /^\d{5,6}$/,                 example: '700000' },
  AE: { re: /^.{0,20}$/,                 example: '(optional)' },
  SA: { re: /^\d{5}$/,                   example: '11564' },
  IL: { re: /^\d{5}(\d{2})?$/,           example: '6100000' },
  TR: { re: /^\d{5}$/,                   example: '34000' },
  RU: { re: /^\d{6}$/,                   example: '101000' },
  UA: { re: /^\d{5}$/,                   example: '01001' },
  BR: { re: /^\d{5}-?\d{3}$/,            example: '01310-100' },
  MX: { re: /^\d{5}$/,                   example: '06000' },
  AR: { re: /^[A-Z]?\d{4}[A-Z]{0,3}$/i,  example: 'C1425' },
  CL: { re: /^\d{7}$/,                   example: '8320000' },
  CO: { re: /^\d{6}$/,                   example: '110111' },
  ZA: { re: /^\d{4}$/,                   example: '8001' },
  EG: { re: /^\d{5}$/,                   example: '11511' },
  NG: { re: /^\d{6}$/,                   example: '100001' },
  KE: { re: /^\d{5}$/,                   example: '00100' },
};

function validateZip(zip, countryCode) {
  const trimmed = (zip || '').trim();
  if (!trimmed) return { ok: true };  // empty is allowed (optional)
  const rule = ZIP_RULES[(countryCode || '').toUpperCase()];
  if (!rule) {
    // Default permissive check for unlisted countries
    const okDefault = /^[A-Za-z0-9 \-]{2,12}$/.test(trimmed);
    return okDefault ? { ok: true } : { ok: false, msg: 'Enter a valid postal code (2-12 chars).' };
  }
  return rule.re.test(trimmed)
    ? { ok: true }
    : { ok: false, msg: `Doesn't match this country's format. Example: ${rule.example}` };
}

/* ─── Reusable searchable combobox ─────────────────────────────────── */
const Combobox = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  emptyText = 'No results.',
  disabled = false,
  testId,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={`w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all flex items-center justify-between gap-2 text-sm ${
            disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:border-slate-400'
          } ${className}`}
        >
          <span className={`truncate text-left ${selected ? 'text-slate-900' : 'text-slate-400'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." className="h-10" />
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${opt.value}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${selected?.value === opt.value ? 'opacity-100' : 'opacity-0'}`} />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/* ─── Main address field ──────────────────────────────────────────── */
export const AddressField = ({ field, value, onChange, commonInputClass = '', LabelComponent }) => {
  // value shape: { street, city, state, zip, country }
  // Internally we ALSO track country/state ISO codes so dropdowns work cleanly,
  // but the DB representation uses the human-readable names (matches existing schema).
  const safe = value || {};
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');

  // Detect country from IP on mount (only if address is still empty)
  const detectedRef = useRef(false);
  useEffect(() => {
    if (detectedRef.current) return;
    if (safe.country) {
      // already filled — sync codes from existing value
      const c = Country.getAllCountries().find((x) => x.name === safe.country);
      if (c) {
        setCountryCode(c.isoCode);
        if (safe.state) {
          const s = State.getStatesOfCountry(c.isoCode).find((x) => x.name === safe.state);
          if (s) setStateCode(s.isoCode);
        }
      }
      detectedRef.current = true;
      return;
    }
    detectedRef.current = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/geo/detect`);
        const j = await r.json();
        const code = (j?.country_code || '').toUpperCase();
        if (code) {
          const c = Country.getAllCountries().find((x) => x.isoCode === code);
          if (c) {
            setCountryCode(code);
            onChange({ ...safe, country: c.name });
          }
        }
      } catch {
        /* silent */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoised lists
  const countryOptions = useMemo(
    () => Country.getAllCountries().map((c) => ({ value: c.isoCode, label: `${c.flag} ${c.name}` })),
    []
  );
  const stateOptions = useMemo(
    () =>
      countryCode
        ? State.getStatesOfCountry(countryCode).map((s) => ({ value: s.isoCode, label: s.name }))
        : [],
    [countryCode]
  );
  const cityOptions = useMemo(
    () =>
      countryCode && stateCode
        ? City.getCitiesOfState(countryCode, stateCode).map((c) => ({ value: c.name, label: c.name }))
        : [],
    [countryCode, stateCode]
  );

  // Handlers
  const handleCountry = (code) => {
    const c = Country.getAllCountries().find((x) => x.isoCode === code);
    setCountryCode(code);
    setStateCode('');
    setCityQuery('');
    setCitySuggestions([]);
    onChange({ ...safe, country: c?.name || '', state: '', city: '' });
  };
  const handleState = (code) => {
    const s = State.getStatesOfCountry(countryCode).find((x) => x.isoCode === code);
    setStateCode(code);
    setCityQuery('');
    setCitySuggestions([]);
    onChange({ ...safe, state: s?.name || '', city: '' });
  };
  const handleCity = (name) => onChange({ ...safe, city: name });
  const handleStreet = (street) => onChange({ ...safe, street });
  const handleZip = (zip) => onChange({ ...safe, zip });

  /* ─── City / suburb live search via Nominatim (covers newer suburbs that
       the static country-state-city dataset doesn't have, e.g. Deanside) ─── */
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const cityDebounce = useRef(null);

  // Currently-picked state name (for narrowing Nominatim results)
  const stateName = useMemo(() => {
    if (!countryCode || !stateCode) return '';
    const s = State.getStatesOfCountry(countryCode).find((x) => x.isoCode === stateCode);
    return s?.name || '';
  }, [countryCode, stateCode]);

  // Merge static (country-state-city) names with live Nominatim suggestions
  const mergedCityOptions = useMemo(() => {
    const staticNames = cityOptions.map((c) => ({
      name: c.value, postcode: '', source: 'static',
    }));
    const dynamicNames = citySuggestions.map((c) => ({
      name: c.name, postcode: c.postcode || '', source: 'live', state: c.state,
    }));
    // Dedupe by lowercase name
    const seen = new Set();
    const merged = [];
    for (const item of [...dynamicNames, ...staticNames]) {
      const k = item.name.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(item);
      }
    }
    // If user typed something, prefer items that start with / include the query
    const q = (cityQuery || '').trim().toLowerCase();
    if (q) {
      return merged
        .filter((m) => m.name.toLowerCase().includes(q))
        .sort((a, b) => {
          const ai = a.name.toLowerCase().startsWith(q) ? 0 : 1;
          const bi = b.name.toLowerCase().startsWith(q) ? 0 : 1;
          return ai - bi || a.name.localeCompare(b.name);
        });
    }
    return merged.slice(0, 50);
  }, [cityOptions, citySuggestions, cityQuery]);

  const onCityInput = (text) => {
    setCityQuery(text);
    handleCity(text);
    setCityOpen(true);
    if (cityDebounce.current) clearTimeout(cityDebounce.current);
    if (!text || text.length < 2 || !countryCode) {
      setCitySuggestions([]);
      return;
    }
    cityDebounce.current = setTimeout(async () => {
      setCityLoading(true);
      try {
        const params = new URLSearchParams({ q: text, country: countryCode, limit: '10' });
        if (stateName) params.append('state', stateName);
        const r = await fetch(`${API_URL}/api/geo/cities?${params}`);
        const data = await r.json();
        setCitySuggestions(Array.isArray(data) ? data : []);
      } catch {
        setCitySuggestions([]);
      } finally {
        setCityLoading(false);
      }
    }, 300);
  };

  const pickCity = (item) => {
    handleCity(item.name);
    setCityQuery(item.name);
    setCityOpen(false);
    // If the suggestion has a postcode and the user hasn't entered one, auto-fill it
    if (item.postcode && !safe.zip) {
      onChange({ ...safe, city: item.name, zip: item.postcode });
    }
  };

  /* ─── Street autocomplete via Nominatim ─── */
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [streetLoading, setStreetLoading] = useState(false);
  const [streetOpen, setStreetOpen] = useState(false);
  const streetDebounce = useRef(null);

  const onStreetInput = (text) => {
    handleStreet(text);
    if (streetDebounce.current) clearTimeout(streetDebounce.current);
    if (!text || text.length < 3) {
      setStreetSuggestions([]);
      setStreetOpen(false);
      return;
    }
    streetDebounce.current = setTimeout(async () => {
      setStreetLoading(true);
      try {
        const params = new URLSearchParams({ q: text });
        if (countryCode) params.append('country', countryCode);
        const r = await fetch(`${API_URL}/api/geo/autocomplete?${params}`);
        const data = await r.json();
        setStreetSuggestions(Array.isArray(data) ? data : []);
        setStreetOpen(true);
      } catch {
        setStreetSuggestions([]);
      } finally {
        setStreetLoading(false);
      }
    }, 350);
  };

  const pickSuggestion = (s) => {
    const c = s.country_code ? Country.getAllCountries().find((x) => x.isoCode === s.country_code) : null;
    const st = c && s.state ? State.getStatesOfCountry(c.isoCode).find((x) => x.name.toLowerCase() === s.state.toLowerCase()) : null;
    if (c) setCountryCode(c.isoCode);
    if (st) setStateCode(st.isoCode);
    onChange({
      street: s.street || safe.street,
      city: s.city || safe.city || '',
      state: (st && st.name) || s.state || safe.state || '',
      zip: s.zip || safe.zip || '',
      country: (c && c.name) || s.country || safe.country || '',
    });
    setStreetOpen(false);
  };

  return (
    <div key={field.id} className="space-y-3 pt-2">
      {LabelComponent}

      {/* Country (first — cascades) */}
      <div>
        <p className="text-xs font-medium text-slate-600 mb-1.5" data-testid={`addr-country-label-${field.id}`}>{(field.sublabels || {}).country || 'Country'}</p>
        <Combobox
          value={countryCode}
          onChange={handleCountry}
          options={countryOptions}
          placeholder="Select country"
          testId={`addr-country-${field.id}`}
        />
      </div>

      {/* Street with autocomplete */}
      <div className="relative">
        <p className="text-xs font-medium text-slate-600 mb-1.5" data-testid={`addr-street-label-${field.id}`}>{(field.sublabels || {}).street || 'Street Address'}</p>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            placeholder={(field.sub_placeholders || {}).street || 'Start typing your address...'}
            value={safe.street || ''}
            onChange={(e) => onStreetInput(e.target.value)}
            onFocus={() => streetSuggestions.length && setStreetOpen(true)}
            onBlur={() => setTimeout(() => setStreetOpen(false), 200)}
            className={`${commonInputClass} pl-10`}
            data-testid={`addr-street-${field.id}`}
            autoComplete="off"
          />
          {streetLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
          )}
        </div>
        {streetOpen && streetSuggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-72 overflow-auto">
            {streetSuggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(s)}
                className="w-full text-left px-4 py-3 hover:bg-indigo-50 border-b border-slate-100 last:border-0 transition-colors"
                data-testid={`addr-suggestion-${field.id}-${i}`}
              >
                <p className="text-sm font-medium text-slate-900 truncate">{s.street || s.display}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[s.city, s.state, s.country].filter(Boolean).join(', ')}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* State + City row (cascading) */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1.5" data-testid={`addr-state-label-${field.id}`}>{(field.sublabels || {}).state || 'State / Province'}</p>
          <Combobox
            value={stateCode}
            onChange={handleState}
            options={stateOptions}
            placeholder={countryCode ? 'Select state' : 'Select country first'}
            disabled={!countryCode}
            testId={`addr-state-${field.id}`}
          />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1.5" data-testid={`addr-city-label-${field.id}`}>{(field.sublabels || {}).city || 'City / Suburb'}</p>
          <div className="relative">
            <Input
              placeholder={(field.sub_placeholders || {}).city || (stateCode ? 'Type your city or suburb…' : 'Select state first')}
              value={cityQuery || safe.city || ''}
              onChange={(e) => onCityInput(e.target.value)}
              onFocus={() => {
                if ((cityQuery || safe.city) && (mergedCityOptions.length || citySuggestions.length)) setCityOpen(true);
                else if (countryCode) setCityOpen(true);
              }}
              onBlur={() => setTimeout(() => setCityOpen(false), 200)}
              disabled={!countryCode}
              className={`${commonInputClass} ${!countryCode ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''}`}
              data-testid={`addr-city-input-${field.id}`}
              autoComplete="off"
            />
            {cityLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
            )}
            {cityOpen && mergedCityOptions.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-72 overflow-auto">
                {mergedCityOptions.slice(0, 12).map((item, i) => (
                  <button
                    key={`${item.name}-${i}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickCity(item)}
                    className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 border-b border-slate-100 last:border-0 transition-colors flex items-center justify-between gap-3"
                    data-testid={`addr-city-suggestion-${field.id}-${i}`}
                  >
                    <span className="text-sm text-slate-900 truncate">{item.name}</span>
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      {item.postcode || (item.source === 'live' ? 'OSM' : '')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Postal / ZIP (optional, country-aware validation) */}
      {(() => {
        const zipCheck = validateZip(safe.zip, countryCode);
        return (
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center justify-between gap-3" data-testid={`addr-zip-label-${field.id}`}>
              <span>
                {(field.sublabels || {}).zip || 'Postal / Zip Code'}{' '}
                <span className="text-slate-400 font-normal">(optional)</span>
              </span>
              {!zipCheck.ok && (
                <span className="text-red-600 font-medium" data-testid={`addr-zip-error-${field.id}`}>
                  {zipCheck.msg}
                </span>
              )}
            </p>
            <Input
              placeholder={(field.sub_placeholders || {}).zip || 'Optional'}
              value={safe.zip || ''}
              onChange={(e) => handleZip(e.target.value)}
              className={`${commonInputClass} ${!zipCheck.ok ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
              data-testid={`addr-zip-${field.id}`}
              aria-invalid={!zipCheck.ok}
            />
          </div>
        );
      })()}
    </div>
  );
};

export default AddressField;
